import { Link } from "react-router-dom";
import { Car, UtensilsCrossed, Camera, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatINR } from "@/lib/format";
import { imageAtWidth, imageSrcSet } from "@/lib/images";

export function TourCard({ tour }) {
  const priced = tour.price_mode === "fixed";

  return (
    <div
      data-testid="tour-card"
      className="tour-card group flex flex-col overflow-hidden rounded-xl border bg-white shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-soft)] sm:flex-row"
    >
      <Link to={`/packages/${tour.slug}`} data-testid={`tour-image-${tour.slug}`} className="tour-card-image relative block h-48 w-full shrink-0 overflow-hidden sm:h-auto sm:w-44 md:w-52">
        <img
          src={imageAtWidth(tour.cover_image, 640)}
          srcSet={imageSrcSet(tour.cover_image, [240, 480, 640])}
          sizes="(max-width: 639px) 108px, 256px"
          width="640"
          height="480"
          decoding="async"
          alt={tour.tour_title}
          loading="lazy"
          className="h-full w-full object-cover transition-opacity duration-300 group-hover:opacity-95"
        />
        <Badge className="absolute left-2 top-2 bg-white/95 text-slate-800 hover:bg-white/95">{tour.region_tag}</Badge>
      </Link>

      <div className="tour-card-content flex min-w-0 flex-1 flex-col gap-2 p-4">
        <Link to={`/packages/${tour.slug}`} data-testid={`tour-title-${tour.slug}`} className="inline-flex min-h-11 items-center">
          <h3 className="text-base font-semibold leading-snug text-slate-900 sm:text-lg font-display">
            {tour.tour_title}
          </h3>
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="border-[hsl(var(--brand-primary))]/30 text-[hsl(var(--brand-primary))]">
            {tour.duration.nights} Nights / {tour.duration.days} Days
          </Badge>
        </div>
        <div className="-mx-1 flex flex-wrap gap-1.5 px-1 py-0.5">
          {tour.routing.map((city, idx) => (
            <span key={idx} className="route-pill">
              {city}
              {idx < tour.routing.length - 1 && <ArrowRight className="ml-1 h-2.5 w-2.5" />}
            </span>
          ))}
        </div>
        <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Car className="h-3.5 w-3.5" /> {tour.vehicle_type.split("(")[0].trim()}</span>
          <span className="flex items-center gap-1"><UtensilsCrossed className="h-3.5 w-3.5" /> Meals Included</span>
          <span className="flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> Sightseeing</span>
        </div>
      </div>

      <div className="tour-card-price flex w-full flex-col justify-between gap-3 border-t p-4 sm:w-56 sm:border-l sm:border-t-0">
        <div>
          {priced ? (
            <>
              <p className="text-xs text-slate-500">Starting Price Per Person</p>
              <p data-testid="tour-card-price" className="font-display text-2xl font-bold text-slate-900">
                {formatINR(tour.starting_price)}
              </p>
            </>
          ) : (
            <>
              <Badge data-testid="tour-card-price-on-request-badge" className="bg-[hsl(var(--brand-primary))]/10 text-[hsl(var(--brand-primary))] border border-[hsl(var(--brand-primary))]/20">
                Price on Request
              </Badge>
              <p className="mt-1.5 text-xs text-slate-500">Share your dates to get a custom quote</p>
            </>
          )}
        </div>
        <Button asChild data-testid="tour-card-view-details-button" className="min-h-11 w-full bg-[hsl(var(--brand-accent))] text-slate-900 hover:bg-[hsl(var(--brand-accent))]/90 active:scale-95">
          <Link to={`/packages/${tour.slug}`}>View Full Details</Link>
        </Button>
      </div>
    </div>
  );
}
